import { ProverBarebonesManifest } from "@/types/proverTypes";
import hljs from "highlight.js/lib/common";
import { UAParser } from "ua-parser-js";

// hljsDefineSolidity(hljs);

export function cn(...args: any[]): string {
  return args.filter(Boolean).join(" ");
}

interface EtherscanResponse {
  sources: Record<
    string,
    {
      content: string;
    }
  >;
}

export const clearStorageAndCookies = () => {
  // overkill lol
  if (typeof window !== undefined) {
    // Clear local storage
    localStorage?.clear();

    // Clear session storage
    sessionStorage?.clear();

    // Clear cookies
    const cookies = document.cookie.split(";");

    for (let cookie of cookies) {
      let eqPos = cookie.indexOf("=");
      let name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
      document.cookie =
        name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/";
      document.cookie =
        name +
        "=;expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=" +
        window.location.hostname;
      document.cookie =
        name +
        "=;expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=." +
        window.location.hostname;
    }
  }
};

export function generateRandomCircuitId(): string {
  // Define the characters that can be included in the random part
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const charactersLength = characters.length;
  let result = "circuit_";

  // Generate a 20-character random string
  for (let i = 0; i < 20; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }

  return result;
}

export const DUMMY_PROOF_VALUE = () => {
  const totalLength = 4092; // Total length of the string
  const prefix = "0x0000000000000000"; // Prefix with '0x' and 16 zeroes
  const randomPartLength = totalLength - prefix.length; // Length of the random part
  let result = prefix;

  const characters = "0123456789abcdef";
  for (let i = 0; i < randomPartLength; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }

  return result;
};

const specialComplex = [
  "constructor",
  "fallback",
  "receive",
  "mapping",
  "array",
  // ... any other special functions that may be introduced in future versions
];

export const typesPrimitive = [
  "address",
  "bool",
  "bytes",
  "uint",
  "int",
  "int24",
  "uint8",
  "uint16",
  "uint24",
  "uint32",
  "uint64",
  "uint128",
  "uint256",
  "int8",
  "int16",
  "int32",
  "int64",
  "int128",
  "int256",
  "byte", // deprecated in favor of 'bytes1'
  "bytes1",
  "bytes2",
  "bytes3",
  "bytes4",
  "bytes5",
  "bytes6",
  "bytes7",
  "bytes8",
  "bytes9",
  "bytes10",
  "bytes11",
  "bytes12",
  "bytes13",
  "bytes14",
  "bytes15",
  "bytes16",
  "bytes17",
  "bytes18",
  "bytes19",
  "bytes20",
  "bytes21",
  "bytes22",
  "bytes23",
  "bytes24",
  "bytes25",
  "bytes26",
  "bytes27",
  "bytes28",
  "bytes29",
  "bytes30",
  "bytes31",
  "bytes32",
  "fixed",
  "ufixed", // These are not fully supported yet.
];

// also going to include own types like mapping and contstructor in here
export const typesModifiers = [
  "public",
  "external",
  "internal",
  "private",
  "pure",
  "view",
  "payable",
  "constant", // This is deprecated in favor of 'view' and 'pure', but may still be used in older contracts.
  "immutable",
  "override",
  "virtual",

  // There can also be user-defined modifiers which won't be in this list
];

const controls = [
  //   'import',
  //   'emit',
  //   'if',
  //   'else',
  //   'while',
  //   'do',
  //   'for',
  //   'break',
  //   'continue',
  //   'return',
  //   'throw',
  //   'revert',
  //   'assert',
  "require",
];

export const codeHighlight = (text: string, extension: string) => {
  return hljs.highlight(text, {
    language: extension,
    ignoreIllegals: true,
  });
};

export const highlightCode = (value: string, language) => {
  let highlightedCode = codeHighlight(value, language).value;

  if (language === "Solidity") {
    const functionNamePattern = /function\s+([a-zA-Z_][a-zA-Z0-9_]*)/g;
    highlightedCode = highlightedCode.replace(
      functionNamePattern,
      (match, p1) => {
        return `function <span class='hljs-name'>${p1}</span>`;
      }
    );

    typesPrimitive.forEach((type) => {
      const typeRegex = new RegExp(`\\b${type}\\b`, "g");
      highlightedCode = highlightedCode.replace(
        typeRegex,
        `<span class='hljs-type-primitive'>$&</span>`
      );
    });

    controls.forEach((control) => {
      const controlRegex = new RegExp(`\\b${control}\\b`, "g");
      highlightedCode = highlightedCode.replace(
        controlRegex,
        `<span class='hljs-type-control'>$&</span>`
      );
    });

    // Wrap type modifier keywords with hljs-type-modifier class
    typesModifiers.forEach((modifier) => {
      const modifierRegex = new RegExp(`\\b${modifier}\\b`, "g");
      highlightedCode = highlightedCode.replace(
        modifierRegex,
        `<span class='hljs-type-modifier'>$&</span>`
      );
    });

    // Wrap type modifier keywords with hljs-type-modifier class
    specialComplex.forEach((complex) => {
      const complexRegex = new RegExp(`\\b${complex}\\b`, "g");
      highlightedCode = highlightedCode.replace(
        complexRegex,
        `<span class='hljs-type-complex'>$&</span>`
      );
    });

    // Wrap line numbers
  }
  // let highlightedWithNumbers = highlightedCode
  //   .split("\n")
  //   .map((line, i) => `<span class='line-number'>${i + 1}</span>${line}`)
  //   .join("\n");

  return highlightedCode;
};

export function insertFunctionIntoContract(
  contractString: string,
  functionString: string
) {
  const placeholder = contractString.indexOf("{") + 1; // Find the position after the first '{'
  const beforeFunction = contractString.substring(0, placeholder);
  const afterFunction = contractString.substring(placeholder);

  // Combine the strings, adding a newline for formatting
  const combinedString =
    beforeFunction + "\n" + functionString + "\n" + afterFunction;

  return combinedString;
}

export function extractSolcVersion(solcVersionString: string) {
  const pattern = /^(\d+\.\d+\.\d+)\+/;
  const match = solcVersionString.match(pattern);
  return match ? match[1] : "";
}

export const extractFunctionName = (code: string) => {
  // Regular expression to match Solidity function definitions
  const regex = /function (\w+)\(/;
  const matches = regex.exec(code);

  if (matches && matches[1]) {
    // Set the first function name found
    return matches[1];
  } else {
    return "";
  }
};

export const formatLongHexString = (value: string) => {
  if (value.length > 8) {
    return `${value.substring(0, 4)}...${value.substring(value.length - 4)}`;
  } else {
    return value;
  }
};

export const isMobileOrTablet = () => {
  const parser = new UAParser();
  const result = parser.getResult();
  const isMobileTablet = ["mobile", "tablet"].includes(result.device.type);

  return isMobileTablet;
};

const postFetch = async (url: string, data: any) => {
  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = {
        message: "Error posting trace data",
        status: response.status,
        statusText: response.statusText,
        errorData,
      };
      console.error(errorMessage);
      return errorMessage;
    }

    // Check if response status is 204 (No Content)
    if (response.status === 204) {
      console.log("No content response");
      return null;
    }

    return response;
  } catch (error) {
    const errorMessage = {
      message: "Network or fetch error",
      error,
    };
    console.error(errorMessage);
    return errorMessage;
  }
};

export const getCurrentRFC3339Time = (): string => {
  return new Date().toISOString();
};

export const sessionIDToTraceID = (sessionID: string) =>
  (sessionID.replace(/-/g, "").match(/.{2}/g) || [])
    .map((byte) => parseInt(byte, 16).toString(16).padStart(2, "0"))
    .join("");

// trace for verify function call for mobile tls example
export const mobileTLSVerifyTrace = async (
  session_id,
  start_time,
  end_time
) => {
  // only collect trace in prod
  if (process.env.NODE_ENV !== "production") return null;
  const url = "https://metrics.pluto.xyz/traces";
  const data = [
    {
      trace_id: sessionIDToTraceID(session_id),
      spans: [
        {
          span_id: 40000,
          name: "tlsnotary/mobiletls/verify",
          start_time,
          end_time,
          labels: {
            "/tlsnotary/session_id": session_id,
          },
        },
      ],
    },
  ];

  const responseData = await postFetch(url, data);
  return responseData;
};

// trace for prove function call (for oauth)
export const webOAuthProveTrace = async (
  session_id,
  start_time,
  end_time,
  host,
  method,
  httpURL
) => {
  // only collect trace in prod
  if (process.env.NODE_ENV !== "production") return null;
  const url = "https://metrics.pluto.xyz/traces";
  const data = [
    {
      trace_id: sessionIDToTraceID(session_id),
      spans: [
        {
          span_id: 40000,
          name: "tlsnotary/weboauth/prove",
          start_time,
          end_time,
          labels: {
            "/tlsnotary/session_id": session_id,
            "/http/host": host,
            "/http/method": method,
            "/http/url": httpURL,
          },
        },
      ],
    },
  ];

  const responseData = await postFetch(url, data);
  return responseData;
};

// trace for verify function call (for oauth)
export const webOAuthVerifyTrace = async (session_id, start_time, end_time) => {
  // only collect trace in prod
  if (process.env.NODE_ENV !== "production") return null;
  const url = "https://metrics.pluto.xyz/traces";
  const data = [
    {
      trace_id: sessionIDToTraceID(session_id),
      spans: [
        {
          span_id: 40000,
          name: "tlsnotary/weboauth/verify",
          start_time,
          end_time,
          labels: {
            "/tlsnotary/session_id": session_id,
          },
        },
      ],
    },
  ];

  const responseData = await postFetch(url, data);
  return responseData;
};

// trace for prove function call (for oauth)
export const webOAuthProveMetric = async (
  session_id,
  start_time,
  end_time,
  proofGenSuccess
) => {
  // only collect trace in prod
  if (process.env.NODE_ENV !== "production") return null;
  const url = "https://metrics.pluto.xyz/metrics";
  const data = {
    gauge_bools: [
      {
        name: "tlsnotary/weboauth/prove",
        points: [
          {
            time: end_time,
            value: proofGenSuccess, // for success, false for failure
          },
        ],
        // labels: {
        //   "/tlsnotary/session_id": session_id,
        // },
      },
    ],
  };

  const responseData = await postFetch(url, data);
  return responseData;
};

export function isValidUrl(urlString: string): boolean {
  try {
    new URL(urlString);
    return true;
  } catch (e) {
    return false;
  }
}

export function utf8ToBase64(str: string): string {
  const utf8Bytes = new TextEncoder().encode(str);
  const binaryString = Array.from(utf8Bytes)
    .map((byte) => String.fromCharCode(byte))
    .join("");
  return btoa(binaryString);
}

export function ensureManifestStatusAsString(
  manifest: any
): ProverBarebonesManifest {
  return {
    request: {
      method:
        typeof manifest?.request?.method === "string"
          ? manifest.request.method
          : "GET",
      url:
        typeof manifest?.request?.url === "string"
          ? manifest.request.url
          : "https://example.com",
      version:
        typeof manifest?.request?.version === "string"
          ? manifest.request.version
          : "HTTP/1.1",
      headers:
        typeof manifest?.request?.headers === "object" &&
        manifest.request.headers !== null
          ? manifest.request.headers
          : {},
    },
    response: {
      status:
        manifest?.response?.status !== undefined
          ? String(manifest.response.status)
          : "200",
      version:
        typeof manifest?.response?.version === "string"
          ? manifest.response.version
          : "HTTP/1.1",
      message:
        typeof manifest?.response?.message === "string"
          ? manifest.response.message
          : "OK",
      headers:
        typeof manifest?.response?.headers === "object" &&
        manifest.response.headers !== null
          ? manifest.response.headers
          : {},
      body: {
        json: Array.isArray(manifest?.response?.body?.json)
          ? manifest.response.body.json.map((item: any) => {
              if (Array.isArray(item)) {
                return item.map((subItem: any) =>
                  typeof subItem === "string" || typeof subItem === "number"
                    ? subItem
                    : String(subItem)
                );
              }
              return typeof item === "string" || typeof item === "number"
                ? item
                : String(item);
            })
          : [],
      },
    },
  };
}

export const validateManifestBodySelection = (manifest: any): boolean => {
  const json = manifest?.response?.body?.json;
  if (!Array.isArray(json) || json.length === 0) {
    return false;
  }
  if (Array.isArray(json[0])) {
    return json.some((entry: any) => Array.isArray(entry) && entry.length > 0);
  }

  return true;
};
